import React, { useState } from 'react';
import { useQuoteStore } from '@/store/quoteStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ArrowLeft, Plus, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import Border from '@/components/Border';

const SURFACE_BLENDS = [
  { 
    group: 'Traditional', 
    blends: ['Woodland***', 'Butterscotch***', 'Sahara***', 'Sand Dune', 'Maya Beach', 'Valencia***', 'Autumn Country***'] 
  },
  { 
    group: 'Classic', 
    blends: ['Crimson Sands', 'Potala Palace', 'Amroth', 'Russo Azure', 'Galapagos', 'Caspian', 'St Moritz'] 
  },
  { 
    group: 'Modern', 
    blends: ['Eclipse', 'Midnight Sapphire', 'Pebble Beach', 'Oceana', 'Empire', 'Antarctica'] 
  },
  { 
    group: 'Contemporary', 
    blends: ['Arctic Blue', 'Howlite', 'Merlin', 'Liberty', 'Aventurine', 'Atlas'] 
  }
];

const SPECIFICATIONS = [
  'Vehicle traffic - surface only',
  'Vehicle traffic - surface and base',
  'Foot traffic - surface only',
  'Foot traffic - surface and base'
];

const Step2BlendAreas = () => {
  const {
    measurementType,
    blendAreas,
    updateBlendArea,
    addBlendArea,
    removeBlendArea,
    setCurrentStep
  } = useQuoteStore();

  const handleNext = () => {
    // Validate all blend areas
    let allValid;
    
    if (measurementType === 'Square metres (m²)') {
      allValid = blendAreas.every(
        (area) => area.surface_blend && area.quantity && parseFloat(area.quantity) > 0 && area.specification
      );
    } else {
      // For Mixes, only surface_blend and quantity (surface mixes) are required
      allValid = blendAreas.every(
        (area) => area.surface_blend && area.quantity && parseFloat(area.quantity) > 0
      );
    }

    if (!allValid) {
      toast.error('Please complete all blend area details');
      return;
    }

    setCurrentStep(3);
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleRemoveArea = (index) => {
    if (blendAreas.length > 1) {
      removeBlendArea(index);
    }
  };

  const unit = measurementType === 'Square metres (m²)' ? 'm²' : 'mixes';

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
      <h2 className="text-2xl md:text-3xl font-bold text-[#27292E] mb-2">
        Select Your Materials
      </h2>
      <p className="text-gray-600 mb-2">
        If your project is more than one blend colour or more than one base specification, add multiple separate areas to your project.
      </p>
      <p className="text-sm text-gray-600 mb-8 italic">
        <strong>Note:</strong> Blends with *** will use binding quartz in the standard specification.
      </p>

      <div className="space-y-6 mb-6">
        {blendAreas.map((area, index) => (
          <div
            key={`blend-${area.specification || 'new'}-${index}`}
            className="border-2 border-gray-200 rounded-xl p-6 relative"
            data-testid={`blend-area-${index}`}
          >
            {blendAreas.length > 1 && (
              <button
                onClick={() => handleRemoveArea(index)}
                className="absolute top-4 right-4 text-red-500 hover:text-red-700 transition-colors"
                data-testid={`remove-blend-${index}`}
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}

            {blendAreas.length > 1 && (
              <h3 className="text-lg font-bold text-[#27292E] mb-4">
                Area {index + 1}
              </h3>
            )}

            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor={`blend-${index}`} className="text-sm font-medium text-gray-700">
                    Surface Blend *
                  </Label>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button
                        className="flex items-center gap-1 text-xs text-[#2F5E96] hover:text-[#254b7a] font-medium transition-colors"
                        type="button"
                      >
                        <Eye className="w-4 h-4" />
                        View Blends
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#2F5E96]">
                      <DialogHeader>
                        <DialogTitle className="text-2xl font-bold text-white">Surface Blends</DialogTitle>
                      </DialogHeader>
                      <div className="mt-4">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_resin-quote-calc/artifacts/h3qj1313_b28c5920-5008-446f-a2d9-09ec4fb00671.jpeg"
                          alt="Surface Blends Brochure"
                          className="w-full h-auto rounded-lg"
                        />
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <Select
                  value={area.surface_blend}
                  onValueChange={(value) => updateBlendArea(index, 'surface_blend', value)}
                >
                  <SelectTrigger
                    id={`blend-${index}`}
                    className="h-14 text-lg border-2 focus:border-[#2F5E96] rounded-xl"
                    data-testid={`surface-blend-${index}`}
                  >
                    <SelectValue placeholder="Select a surface blend" />
                  </SelectTrigger>
                  <SelectContent>
                    {SURFACE_BLENDS.map((group) => (
                      <SelectGroup key={group.group}>
                        <SelectLabel className="text-sm font-bold text-[#2F5E96] bg-[#E0E9EB]">
                          {group.group}
                        </SelectLabel>
                        {group.blends.map((blend) => (
                          <SelectItem key={blend} value={blend}>
                            {blend}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* For Square Metres - show quantity and specification */}
              {measurementType === 'Square metres (m²)' && (
                <>
                  <div>
                    <Label htmlFor={`quantity-${index}`} className="text-sm font-medium text-gray-700 mb-2 block">
                      Area (m²) *
                    </Label>
                    <Input
                      id={`quantity-${index}`}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Enter area in m²"
                      value={area.quantity}
                      onChange={(e) => updateBlendArea(index, 'quantity', e.target.value)}
                      className="h-14 text-lg border-2 focus:border-[#2F5E96] rounded-xl"
                      data-testid={`quantity-${index}`}
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor={`spec-${index}`} className="text-sm font-medium text-gray-700">
                        Specification Required *
                      </Label>
                      <Dialog>
                        <DialogTrigger asChild>
                          <button
                            className="flex items-center gap-1 text-xs text-[#2F5E96] hover:text-[#254b7a] font-medium transition-colors"
                            type="button"
                          >
                            <Eye className="w-4 h-4" />
                            Installation Guide
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#2F5E96]">
                          <DialogHeader>
                            <DialogTitle className="text-2xl font-bold text-white">ProBound System Installation Guide</DialogTitle>
                          </DialogHeader>
                          <div className="mt-4">
                            <img 
                              src="https://customer-assets.emergentagent.com/job_resin-quote-calc/artifacts/fsee5bl9_70742e4c-8472-4610-999b-3826e7bf392b.jpeg"
                              alt="ProBound System Installation Guide"
                              className="w-full h-auto rounded-lg"
                            />
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <Select
                      value={area.specification}
                      onValueChange={(value) => updateBlendArea(index, 'specification', value)}
                    >
                      <SelectTrigger
                        id={`spec-${index}`}
                        className="h-14 text-lg border-2 focus:border-[#2F5E96] rounded-xl"
                        data-testid={`specification-${index}`}
                      >
                        <SelectValue placeholder="Select specification" />
                      </SelectTrigger>
                      <SelectContent>
                        {SPECIFICATIONS.map((spec) => (
                          <SelectItem key={spec} value={spec}>
                            {spec}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {/* For Mixes - show surface mixes and base mixes separately */}
              {measurementType === 'Mixes' && (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor={`surface-mixes-${index}`} className="text-sm font-medium text-gray-700">
                        Surface Mixes *
                      </Label>
                      <Dialog>
                        <DialogTrigger asChild>
                          <button
                            className="flex items-center gap-1 text-xs text-[#2F5E96] hover:text-[#254b7a] font-medium transition-colors"
                            type="button"
                          >
                            <Eye className="w-4 h-4" />
                            Installation Guide
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#2F5E96]">
                          <DialogHeader>
                            <DialogTitle className="text-2xl font-bold text-white">ProBound System Installation Guide</DialogTitle>
                          </DialogHeader>
                          <div className="mt-4">
                            <img 
                              src="https://customer-assets.emergentagent.com/job_resin-quote-calc/artifacts/fsee5bl9_70742e4c-8472-4610-999b-3826e7bf392b.jpeg"
                              alt="ProBound System Installation Guide"
                              className="w-full h-auto rounded-lg"
                            />
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <Input
                      id={`surface-mixes-${index}`}
                      type="number"
                      step="1"
                      min="0"
                      placeholder="Enter number of surface mixes"
                      value={area.quantity}
                      onChange={(e) => updateBlendArea(index, 'quantity', e.target.value)}
                      className="h-14 text-lg border-2 focus:border-[#2F5E96] rounded-xl"
                      data-testid={`surface-mixes-${index}`}
                    />
                  </div>

                  <div>
                    <Label htmlFor={`base-mixes-${index}`} className="text-sm font-medium text-gray-700 mb-2 block">
                      Base Mixes (Optional)
                    </Label>
                    <Input
                      id={`base-mixes-${index}`}
                      type="number"
                      step="1"
                      min="0"
                      placeholder="Enter number of base mixes (if needed)"
                      value={area.base_mixes || ''}
                      onChange={(e) => updateBlendArea(index, 'base_mixes', e.target.value)}
                      className="h-14 text-lg border-2 focus:border-[#2F5E96] rounded-xl"
                      data-testid={`base-mixes-${index}`}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <Button
        onClick={addBlendArea}
        variant="outline"
        className="w-full mb-6 h-14 text-lg font-medium rounded-xl border-2 border-dashed border-[#2F5E96] text-[#2F5E96] hover:bg-[#2F5E96]/5"
        data-testid="add-blend-area-button"
      >
        <Plus className="w-5 h-5 mr-2" />
        Add Another Area
      </Button>

      {/* Border Section */}
      <Border />

      <div className="flex gap-4 mt-6">
        <Button
          onClick={handleBack}
          variant="outline"
          className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#2F5E96] text-[#2F5E96] hover:bg-[#2F5E96]/5"
          data-testid="back-button-step2"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>
        <Button
          onClick={handleNext}
          className="flex-1 bg-[#2F5E96] hover:bg-[#254b7a] text-white h-14 text-lg font-bold rounded-xl transition-transform active:scale-95"
          data-testid="next-button-step2"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default Step2BlendAreas;